import React, { useState, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useAuth } from "providers/CustomAuthProvider";
import { getAllClients } from "api/clients";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import useShowSnackbar from "hooks/useShowSnackbar";

const ClientDropdown = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    isSystemAdmin,
    setClientId,
    setClientName,
    clientId,
    defaultClientId,
  } = useAuth();
  const showSnackbar = useShowSnackbar();

  // Tracks the last client id we know is reflected in the dropdown UI.
  // Used to distinguish user-initiated changes (-> snackbar + dispatch) from
  // external syncs (Redux -> form) caused by remounts/route changes.
  const previousClientIdRef = useRef(clientId);
  // Suppresses the snackbar/dispatch when we programmatically sync the form
  // from Redux (e.g. on mount or when clients list arrives).
  const isSyncingFromReduxRef = useRef(false);

  // FormInput (select) uses HyperSelect under the hood, which is controlled
  // via react-hook-form `control` and stores the entire selected option
  // object as the field value (not just the id).
  const { control, setValue } = useForm({
    defaultValues: {
      client: null,
    },
  });

  const selectedOption = useWatch({ control, name: "client" });
  const selectedValue = selectedOption ? selectedOption.value : undefined;

  // Handle USER-initiated selection changes (option object -> id).
  // Programmatic syncs from Redux are flagged via isSyncingFromReduxRef so
  // we don't fire snackbars or redundant dispatches on remount.
  useEffect(() => {
    if (selectedValue === undefined) return;

    if (isSyncingFromReduxRef.current) {
      isSyncingFromReduxRef.current = false;
      previousClientIdRef.current = selectedValue;
      return;
    }

    if (previousClientIdRef.current !== selectedValue) {
      setClientId(selectedValue);
      previousClientIdRef.current = selectedValue;

      const displayName = selectedOption ? selectedOption.text : "";
      setClientName(displayName || null);
      showSnackbar(`Client changed to ${displayName}`, "success");
    }
  }, [selectedValue]);

  // Fetch clients on mount (or when system-admin status flips on).
  useEffect(() => {
    if (!isSystemAdmin) return;

    const fetchClients = async () => {
      setLoading(true);
      try {
        const clientList = await getAllClients();

        const formattedClients = clientList.data.map((client) => ({
          value: client.clientID,
          text: client.clientName,
        }));

        setClients(formattedClients);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [isSystemAdmin]);

  // Continuously sync the dropdown's displayed value from Redux (single
  // source of truth). This runs whenever clientId, defaultClientId, or the
  // clients list changes - including when the component remounts after a
  // route change (e.g. navigating to /dashboard). Without this, the form's
  // local state resets to its defaultValue on every remount.
  useEffect(() => {
    if (!isSystemAdmin) return;
    if (!clients.length) return;

    const desiredId = (clientId || defaultClientId)?.toLowerCase();
    if (!desiredId) return;

    // If the form already reflects the desired id, nothing to do.
    if (selectedValue === desiredId) {
      previousClientIdRef.current = desiredId;
      return;
    }

    const matched = clients.find((c) => c.value?.toLowerCase() === desiredId);
    if (matched) {
      isSyncingFromReduxRef.current = true;
      previousClientIdRef.current = desiredId;
      setValue("client", matched);
    }
  }, [clientId, defaultClientId, clients, isSystemAdmin, selectedValue, setValue]);

  if (!isSystemAdmin) {
    return null;
  }

  return (
    <div className="client-dropdown-container d-flex align-items-center gap-2">
      <span className="text-nowrap fw-semibold">Select Company</span>

      <FormInput
        type="select"
        name="client"
        control={control}
        options={clients}
        getOptionLabel={(option) => option.text}
        getOptionValue={(option) => option.value}
        placeholder="Select client"
        isLoading={loading}
        containerClass="mb-0"
        menuPlacement="auto"
        maxMenuHeight={300}
      />
    </div>

  );
};

export default ClientDropdown;
